// pages/UserAdmin/crm/theme.js
//
// Tokens visuais do CRM. Único ponto de verdade — qualquer componente do
// CRM lê daqui.
//
// Out/26 (CRM moderno, do Claude Design "CRM Moderno.dc.html"): os valores
// viraram `var(--ch-*)` / `var(--cx-*)` — os do ChatPage.css e do crm.css —,
// o mesmo truque do chat: trocar de tema é trocar um atributo no <html>, e
// nenhum `sx` que lê `CRM_THEME` muda. Até aqui eram hex fixos do tema claro
// ("o CRM não tem dark ainda").
//
// As cores de interesse, reunião, etiqueta e pasta são as do design: Alto
// azul, Médio ciano, Baixo cinza, Frio vermelho, "Não classificado"
// tracejado (campo em branco, não mais um nível); etiquetas e pastas usam a
// mesma família de cor do backend (green/blue/purple/red/amber/pink).

import { createTheme } from '@mui/material/styles';

export const SYNE = "'Syne', system-ui, sans-serif";
export const OUTFIT = "'Outfit', system-ui, sans-serif";

// Sentinela de "não classificado" — no banco é NULL (models.INTEREST_UNSET).
export const UNSET = 'NAO_CLASSIFICADO';

const tk = (k) => ({ dot: `var(--ch-${k})`, color: `var(--ch-${k}-d)`, bg: `var(--ch-${k}-bg)`, border: `var(--ch-${k}-b)` });

export const CRM_THEME = {
  // Outfit no texto e nos números; Syne só nos títulos, nas iniciais e nas
  // letras da lista — como no design.
  font: { ui: OUTFIT, num: OUTFIT, display: SYNE },

  bg: 'var(--ch-bg)',
  card: 'var(--ch-card)',
  surface: 'var(--ch-surf)',
  inset: 'var(--ch-inset)',
  input: 'var(--cx-input)',
  hover: 'var(--cx-hover)',
  border: 'var(--ch-bdr)',
  border2: 'var(--ch-bdr2)',

  // Texto: corpo, secundário, legenda e o apagado (traço de célula vazia).
  text: 'var(--ch-text)',
  text2: 'var(--ch-text2)',
  text3: 'var(--cx-cap)',
  faint: 'var(--ch-text3)',

  brand: 'var(--ch-green)',
  brandHover: 'var(--ch-green-hover)',
  brandDark: 'var(--ch-green-d)',
  brandBg: 'var(--ch-green-bg)',
  brandBorder: 'var(--ch-green-b)',
  brandGlow: 'var(--ch-green-glow)',
  onBrand: 'var(--cx-on-green)',
  selected: 'var(--cx-sel)',

  interest: {
    ALTO: tk('blue'),
    MEDIO: { dot: 'var(--cx-cyan)', color: 'var(--cx-cyan-d)', bg: 'var(--cx-cyan-bg)', border: 'var(--cx-cyan-b)' },
    BAIXO: { dot: 'var(--ch-text3)', color: 'var(--ch-text2)', bg: 'var(--ch-inset)', border: 'var(--ch-bdr2)' },
    FRIO: tk('red'),
    // Tracejado e sem fundo: precisa ler como campo em branco. O par
    // `ativo` é o do chip-filtro ligado, que precisa se distinguir do
    // desligado (o cinza do badge de linha não dá contraste nenhum).
    [UNSET]: {
      dot: 'transparent', color: 'var(--cx-cap)', bg: 'transparent', border: 'var(--ch-bdr2)', dashed: true,
      ativo: { bg: 'var(--ch-inset)', color: 'var(--ch-text)', border: 'var(--cx-cap)' },
      barra: 'repeating-linear-gradient(135deg, var(--ch-bdr2) 0 3px, transparent 3px 6px)',
    },
  },

  meeting: {
    NAO_AGENDADA: { color: 'var(--ch-text2)', bg: 'var(--ch-inset)', border: 'var(--ch-bdr)' },
    PENDENTE: tk('amber'),
    CONFIRMADA: tk('green'),
    NO_SHOW: tk('red'),
  },

  // Silêncio prolongado — âmbar (alerta discreto).
  silence: tk('amber'),

  shadow: {
    sm: 'var(--cx-shadow)',
    lg: 'var(--cx-shadow-lg)',
    // A barra de lote é escura nos dois temas (vidro sobre a página).
    dark: '0 18px 48px rgba(0,0,0,0.38), 0 2px 8px rgba(0,0,0,0.22)',
  },

  radius: { sm: 10, md: 14, lg: 18, xl: 22 },
};

export const INTEREST_LABELS = {
  [UNSET]: 'Não classificado', ALTO: 'Alto', MEDIO: 'Médio', BAIXO: 'Baixo', FRIO: 'Frio',
};
// Níveis reais — o que aparece nos menus de classificação.
export const INTEREST_ORDER = ['ALTO', 'MEDIO', 'BAIXO', 'FRIO'];

export const MEETING_LABELS = {
  NAO_AGENDADA: 'Não agendada', PENDENTE: 'Pendente', CONFIRMADA: 'Confirmada', NO_SHOW: 'No-show',
};
export const MEETING_OPTS = ['NAO_AGENDADA', 'PENDENTE', 'CONFIRMADA', 'NO_SHOW'];

// Threshold do silence badge — decidido produto/set/26. Contato sem
// inbound há mais de 5 dias E com meeting_status=PENDENTE (ou
// interest_status ALTO/MEDIO) merece um badge.
export const SILENCE_THRESHOLD_DAYS = 5;

// As seis cores que o backend aceita pra etiqueta e pasta. Fallback pra
// não quebrar se algum dia chegar uma fora da lista.
const CORES = new Set(['green', 'blue', 'purple', 'red', 'amber', 'pink']);
export function labelColor(color) {
  return tk(CORES.has(color) ? color : 'green');
}
export function folderColor(color) {
  return tk(CORES.has(color) ? color : 'blue');
}

// Avatar: os 8 pares do ChatPage.css (`--ch-avN-*`), escolhidos por hash
// do id — o mesmo contato tem a mesma cor em qualquer tela e nos dois temas.
export function avatarDe(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const n = (h % 8) + 1;
  return { bg: `var(--ch-av${n}-bg)`, fg: `var(--ch-av${n}-fg)` };
}

export function initials(name) {
  return (name || '?').trim()[0]?.toUpperCase() || '?';
}

// Número pra ler, como no design: "+55 21 99812-4410". Só apresentação — o
// banco guarda os dígitos, a busca e a exportação usam os dígitos. Número de
// fora do Brasil (ou fora do formato 55 + DDD + 8/9 dígitos) sai com "+" e
// os dígitos, sem adivinhar agrupamento.
export function formatarFone(fone) {
  const d = String(fone || '').replace(/\D/g, '');
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) {
    const n = d.slice(4);
    return `+55 ${d.slice(2, 4)} ${n.slice(0, n.length - 4)}-${n.slice(-4)}`;
  }
  return d ? `+${d}` : '';
}

// Rótulo de seção ("DADOS DO CONTATO"): caixa-alta espaçada, cor de legenda.
export const rotulo = {
  fontFamily: OUTFIT, fontSize: 12, fontWeight: 600, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: 'var(--cx-cap)',
};

// Tema do MUI pros diálogos, menus e campos do CRM. Sem ele, no escuro, os
// diálogos abriam brancos e os TextField ficavam com texto preto sobre o
// cartão escuro. Valores concretos (não `var()`): o MUI calcula hover e
// transparências a partir da cor, e `alpha(var(...))` não existe.
export function temaMui(modo) {
  const escuro = modo === 'dark';
  return createTheme({
    palette: escuro ? {
      mode: 'dark',
      primary: { main: '#00d67c', contrastText: '#060d14' },
      error: { main: '#f87171' },
      warning: { main: '#f59e0b' },
      success: { main: '#00d67c' },
      info: { main: '#38bdf8' },
      background: { default: '#070b13', paper: '#0a0f1a' },
      text: { primary: '#e8edf5', secondary: '#8090a4', disabled: '#3e4d60' },
      divider: 'rgba(255, 255, 255, 0.07)',
    } : {
      mode: 'light',
      primary: { main: '#00b865', contrastText: '#04140c' },
      error: { main: '#dc2626' },
      warning: { main: '#d97706' },
      success: { main: '#00b865' },
      info: { main: '#2563eb' },
      background: { default: '#f0f3f8', paper: '#ffffff' },
      text: { primary: '#1a2535', secondary: '#4b5e72', disabled: '#94a3b8' },
      divider: '#e2e8f0',
    },
    typography: { fontFamily: OUTFIT },
    shape: { borderRadius: 10 },
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 18, border: '1px solid var(--ch-bdr)', boxShadow: 'var(--cx-shadow-lg)' },
        },
      },
      // Só o véu dos diálogos: o fundo dos menus também é um Backdrop, só que
      // `invisible` — pintá-lo escurecia a tela inteira a cada menu aberto.
      MuiBackdrop: {
        styleOverrides: {
          root: ({ ownerState }) => (ownerState.invisible ? {} : {
            backgroundColor: 'var(--cx-scrim)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 16, border: '1px solid var(--ch-bdr2)', boxShadow: 'var(--cx-shadow-lg)' },
        },
      },
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontFamily: OUTFIT, fontSize: 12 } } },
    },
  });
}
